import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Check,
  Sparkles,
  QrCode,
  Copy,
  Clock,
  Upload,
  CheckCircle2,
  User,
  Mail,
  Phone,
  X,
  CreditCard,
  Download,
  Image as ImageIcon,
  Video,
  Music,
  Loader2,
  Gift
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getPricingSettings, type CreditPackage } from "@/lib/pricingApi";
import { getPaymentSettings, createStripeOrder, createBeamOrder, checkOrderStatus, type PaymentSettings } from "@/lib/stripeApi";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CREDIT_VERIFICATION_MAX_ATTEMPTS = 8;

const Pricing = () => {
  const navigate = useNavigate();
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);

  // Stripe payment state
  const [stripeQrUrl, setStripeQrUrl] = useState<string | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeExpiresAt, setStripeExpiresAt] = useState<Date | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>('pending');
  const [isTestPayment, setIsTestPayment] = useState(false);

  // Beam payment state
  const [beamQrUrl, setBeamQrUrl] = useState<string | null>(null);
  const [beamLoading, setBeamLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingPackageIndex, setPendingPackageIndex] = useState<number | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const expectedCreditsForOrderRef = useRef<number | null>(null);
  const creditBaselineRef = useRef<number | null>(null);
  const creditVerificationAttemptsRef = useRef<number>(0);

  // Promo claim state
  const [promoEligible, setPromoEligible] = useState(false);
  const [promoCredits, setPromoCredits] = useState(0);
  const [promoTitle, setPromoTitle] = useState("");
  const [promoClaiming, setPromoClaiming] = useState(false);
  const [promoClaimed, setPromoClaimed] = useState(false);

  // Load pricing and payment settings from database
  useEffect(() => {
    loadPricing();
    loadPaymentSettings();

    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const settings = await getPricingSettings();
      setCreditPackages(settings.packages);
    } catch (error) {
      console.error("Failed to load pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const settings = await getPaymentSettings();
      setPaymentSettings(settings);
    } catch (error) {
      console.error("Failed to load payment settings:", error);
    }
  };

  const captureCreditBaseline = async () => {
    if (!user?.id) {
      creditBaselineRef.current = null;
      return;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("credits")
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        console.error("Failed to capture credit baseline:", error);
        creditBaselineRef.current = null;
        return;
      }

      const credits = Number((data as any).credits);
      creditBaselineRef.current = Number.isFinite(credits) ? credits : null;
    } catch (error) {
      console.error("Failed to capture credit baseline:", error);
      creditBaselineRef.current = null;
    }
  };

  const verifyOrderCreditsAdded = async (expectedCredits: number): Promise<boolean> => {
    if (!user?.id || creditBaselineRef.current === null) {
      return true;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("credits")
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        console.error("Failed to verify credits from profile:", error);
        return false;
      }

      const currentCredits = Number((data as any).credits);
      if (!Number.isFinite(currentCredits)) return false;

      return currentCredits >= (creditBaselineRef.current + expectedCredits);
    } catch (error) {
      console.error("Failed to verify credits from profile:", error);
      return false;
    }
  };

  const initializeCreditVerification = async (packageCredits: number, packageBonus = 0) => {
    expectedCreditsForOrderRef.current = packageCredits + packageBonus;
    creditVerificationAttemptsRef.current = 0;
    await captureCreditBaseline();
  };

  // Check promo eligibility when user changes
  useEffect(() => {
    if (user) {
      checkPromoEligibility();
    } else {
      setPromoEligible(false);
    }
  }, [user]);

  const checkPromoEligibility = async () => {
    try {
      const { data, error } = await (supabase as any).rpc("check_promo_eligibility");
      if (error) throw error;

      if (data && data.length > 0 && data[0].is_eligible) {
        setPromoEligible(true);
        setPromoCredits(data[0].credits);
        setPromoTitle(data[0].title);
      } else {
        setPromoEligible(false);
      }
    } catch (error) {
      console.error("Error checking promo eligibility:", error);
    }
  };

  const handleClaimPromo = async () => {
    if (promoClaiming || promoClaimed) return;

    setPromoClaiming(true);
    try {
      const { data, error } = await (supabase as any).rpc("claim_promo_credits");
      if (error) throw error;

      if (data && data.length > 0 && data[0].success) {
        setPromoClaimed(true);
        setPromoEligible(false);
        toast({
          title: t("🎉 รับเครดิตสำเร็จ!", "🎉 Credits Claimed!"),
          description: t(`คุณได้รับ ${data[0].credits_received.toLocaleString()} เครดิตแล้ว`, `You received ${data[0].credits_received.toLocaleString()} credits!`),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("ไม่สามารถรับเครดิตได้", "Cannot Claim Credits"),
          description: data?.[0]?.message || t("โปรดลองใหม่อีกครั้ง", "Please try again"),
        });
      }
    } catch (error: any) {
      console.error("Error claiming promo:", error);
      toast({
        variant: "destructive",
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message || t("ไม่สามารถรับเครดิตได้", "Could not claim credits"),
      });
    } finally {
      setPromoClaiming(false);
    }
  };

  // Start polling for Stripe payment status
  const startPaymentPolling = (orderId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await checkOrderStatus(orderId);
        if (result.success) {
          setPaymentStatus(result.status || 'pending');

          if (result.status === 'completed') {
            const expectedCredits = expectedCreditsForOrderRef.current;
            if (expectedCredits !== null && user && creditBaselineRef.current !== null) {
              const creditsAdded = await verifyOrderCreditsAdded(expectedCredits);
              if (!creditsAdded) {
                creditVerificationAttemptsRef.current += 1;
                setPaymentStatus('verifying');

                if (creditVerificationAttemptsRef.current >= CREDIT_VERIFICATION_MAX_ATTEMPTS) {
                  clearInterval(pollingIntervalRef.current!);
                  pollingIntervalRef.current = null;
                  toast({
                    variant: "destructive",
                    title: t("การเติมเครดิตไม่สมบูรณ์", "Credit Update Incomplete"),
                    description: t("ระบบรับชำระเงินแล้ว แต่ยังไม่สามารถยืนยันการเติมเครดิตได้ อาจต้องรอการซิงค์ หรือกรุณาติดต่อทีมสนับสนุน", "Payment was completed, but credits were not confirmed yet. Please wait for sync or contact support."),
                  });
                  return;
                }

                if (creditVerificationAttemptsRef.current === 1) {
                  toast({
                    title: t("กำลังยืนยันเครดิต", "Verifying credits"),
                    description: t("ชำระเงินสำเร็จแล้ว กำลังรอระบบอัปเดตเครดิต", "Payment is successful. Waiting for credits to update."),
                  });
                }
                return;
              }
            }

            // Payment successful!
            clearInterval(pollingIntervalRef.current!);
            pollingIntervalRef.current = null;
            toast({
              title: t("ชำระเงินสำเร็จ!", "Payment Successful!"),
              description: t("เครดิตถูกเติมเข้าบัญชีของคุณแล้ว กำลังไปหน้า Dashboard...", "Credits have been added. Redirecting to Dashboard..."),
            });
            // Redirect to dashboard after short delay
            setTimeout(() => {
              resetStripeState();
              navigate('/dashboard');
            }, 2000);
          } else if (result.status === 'failed' || result.status === 'cancelled') {
            clearInterval(pollingIntervalRef.current!);
            pollingIntervalRef.current = null;
            toast({
              variant: "destructive",
              title: t("การชำระเงินล้มเหลว", "Payment Failed"),
              description: t("กรุณาลองใหม่อีกครั้ง", "Please try again"),
            });
          } else if (result.expired) {
            clearInterval(pollingIntervalRef.current!);
            pollingIntervalRef.current = null;
            toast({
              variant: "destructive",
              title: t("QR Code หมดอายุ", "QR Code Expired"),
              description: t("กรุณาสร้าง QR ใหม่", "Please generate a new QR code"),
            });
            resetStripeState();
          }
        }
      } catch (error) {
        console.error("Error polling payment status:", error);
      }
    }, 3000); // Poll every 3 seconds
  };

  // Reset Stripe payment state
  const resetStripeState = () => {
    setStripeQrUrl(null);
    setCurrentOrderId(null);
    setStripeExpiresAt(null);
    setPaymentStatus('pending');
    setIsTestPayment(false);
    expectedCreditsForOrderRef.current = null;
    creditBaselineRef.current = null;
    creditVerificationAttemptsRef.current = 0;
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Handle Stripe payment creation
  const handleStripePayment = async () => {
    if (selectedPackage === null) return;

    const pkg = creditPackages[selectedPackage];

    // Validation for guest users
    if (!user && !guestEmail) {
      toast({
        variant: "destructive",
        title: t("กรุณาระบุอีเมล", "Email Required"),
        description: t("ต้องระบุอีเมลเพื่อรับใบเสร็จ", "Email is required to receive receipt"),
      });
      return;
    }

    setStripeLoading(true);
    try {
      const result = await createStripeOrder({
        packageId: pkg.id,
        credits: pkg.credits,
        price: pkg.price,
        bonus: pkg.bonus,
        packageName: `${pkg.credits.toLocaleString()} Credits`,
        guestName: guestName || undefined,
        guestEmail: guestEmail || user?.email || undefined,
        guestPhone: guestPhone || undefined,
      });

      if (result.success && result.qrCodeUrl) {
        setStripeQrUrl(result.qrCodeUrl);
        setCurrentOrderId(result.orderId!);
        setStripeExpiresAt(result.expiresAt ? new Date(result.expiresAt) : null);
        setPaymentStatus('pending');
        setIsTestPayment(false); // Regular payment, not test
        await initializeCreditVerification(pkg.credits, pkg.bonus);
        // Start polling for payment status
        startPaymentPolling(result.orderId!);
      } else {
        throw new Error(result.error || 'Failed to create payment');
      }
    } catch (error: any) {
      console.error("Error creating Stripe payment:", error);
      toast({
        variant: "destructive",
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message || t("ไม่สามารถสร้างการชำระเงินได้", "Could not create payment"),
      });
    } finally {
      setStripeLoading(false);
    }
  };


  const handleSaveQR = () => {
    const qrUrl = paymentSettings?.manualQrCodeUrl || "/kbank-bas.jpg";
    const link = document.createElement("a");
    link.href = qrUrl;
    link.download = "payment-qr.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: t("บันทึกรูปภาพแล้ว", "QR Saved"),
      description: t("รูปภาพถูกบันทึกไปยังเครื่องของคุณแล้ว", "QR code has been saved to your device"),
    });
  };

  // Determine payment mode
  const isStripeMode = paymentSettings?.paymentMode === 'stripe';
  const isBeamMode = paymentSettings?.paymentMode === 'beam';

  // Handle Beam payment creation
  const handleBeamPayment = async () => {
    if (selectedPackage === null) return;

    const pkg = creditPackages[selectedPackage];

    // Validation for guest users
    if (!user && !guestEmail) {
      toast({
        variant: "destructive",
        title: t("กรุณาระบุอีเมล", "Email Required"),
        description: t("ต้องระบุอีเมลเพื่อรับใบเสร็จ", "Email is required to receive receipt"),
      });
      return;
    }

    setBeamLoading(true);
    try {
      const result = await createBeamOrder({
        packageId: pkg.id,
        credits: pkg.credits,
        price: pkg.price,
        bonus: pkg.bonus,
        packageName: `${pkg.credits.toLocaleString()} Credits`,
        guestName: guestName || undefined,
        guestEmail: guestEmail || user?.email || undefined,
        guestPhone: guestPhone || undefined,
      });

      if (result.success && result.qrCodeUrl) {
        setBeamQrUrl(result.qrCodeUrl);
        setCurrentOrderId(result.orderId!);
        setStripeExpiresAt(result.expiresAt ? new Date(result.expiresAt) : null);
        setPaymentStatus('pending');
        await initializeCreditVerification(pkg.credits, pkg.bonus);
        // Start polling for payment status (using same function as Stripe)
        startPaymentPolling(result.orderId!);
      } else {
        throw new Error(result.error || 'Failed to create Beam payment');
      }
    } catch (error: any) {
      console.error("Error creating Beam payment:", error);
      toast({
        variant: "destructive",
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message || t("ไม่สามารถสร้างการชำระเงินได้", "Could not create payment"),
      });
    } finally {
      setBeamLoading(false);
    }
  };

  // Reset Beam payment state
  const resetBeamState = () => {
    setBeamQrUrl(null);
    setCurrentOrderId(null);
    setStripeExpiresAt(null);
    setPaymentStatus('pending');
    expectedCreditsForOrderRef.current = null;
    creditBaselineRef.current = null;
    creditVerificationAttemptsRef.current = 0;
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleBuyCredits = (index: number) => {
    if (!user) {
      setPendingPackageIndex(index);
      setShowLoginPrompt(true);
      return;
    }
    setSelectedPackage(index);
    setShowPayment(true);
  };

  const handleCloseLoginPrompt = () => {
    setShowLoginPrompt(false);
    setPendingPackageIndex(null);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: t("ไฟล์ไม่ถูกต้อง", "Invalid file"),
        description: t("กรุณาอัพโหลดไฟล์รูปภาพเท่านั้น", "Please upload image files only")
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: t("ไฟล์ใหญ่เกินไป", "File too large"),
        description: t("ขนาดไฟล์ต้องไม่เกิน 5MB", "File size must not exceed 5MB")
      });
      return;
    }

    setUploading(true);
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `payment-slips/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('payment-slips')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      setUploadedFile(file);
      setIsUploaded(true);
      toast({
        title: t("อัพโหลดสำเร็จ", "Upload successful"),
        description: t("สลิปการโอนเงินถูกอัพโหลดแล้ว", "Payment slip has been uploaded")
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: "destructive",
        title: t("อัพโหลดล้มเหลว", "Upload failed"),
        description: t("ไม่สามารถอัพโหลดไฟล์ได้ กรุณาลองใหม่อีกครั้ง", "Could not upload file. Please try again")
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              {t("ซื้อ", "Buy")} <span className="text-gradient">{t("เครดิต", "Credits")}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("จ่ายตามการใช้งาน ไม่มีสมัครสมาชิก ใช้เท่าที่ต้องการ", "Pay-as-you-go credits. No subscriptions. Use what you need, when you need it.")}
            </p>

          </div>

          {!showPayment ? (
            <>
              {/* Promo Claim Banner */}
              {promoEligible && (
                <div className="max-w-2xl mx-auto mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
                  <Card className="relative overflow-hidden border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-red-500/10">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-orange-500/5 animate-pulse" />
                    <CardContent className="p-6 relative">
                      <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className="shrink-0">
                          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 animate-bounce">
                            <Gift className="h-8 w-8 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 text-center md:text-left">
                          <h3 className="text-xl font-bold text-amber-500 mb-1">
                            🎁 {t("คุณมีสิทธิ์รับเครดิตฟรี!", "You have FREE credits to claim!")}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {promoTitle} - <span className="font-bold text-amber-500">{promoCredits.toLocaleString()}</span> {t("เครดิต", "credits")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("กดปุ่มด้านล่างเพื่อรับเครดิต (ได้เพียงครั้งเดียว)", "Click the button below to claim (one-time only)")}
                          </p>
                        </div>
                        <div className="shrink-0">
                          <Button
                            variant="glow"
                            size="lg"
                            className="gap-2 font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-500/30"
                            onClick={handleClaimPromo}
                            disabled={promoClaiming || promoClaimed}
                          >
                            {promoClaiming ? (
                              <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                {t("กำลังรับ...", "Claiming...")}
                              </>
                            ) : promoClaimed ? (
                              <>
                                <CheckCircle2 className="h-5 w-5" />
                                {t("รับแล้ว", "Claimed")}
                              </>
                            ) : (
                              <>
                                <Gift className="h-5 w-5" />
                                {t("รับเครดิตฟรี!", "Claim Free Credits!")}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Credit Packages */}
              {loading ? (
                <div className="flex justify-center items-center min-h-[400px]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto mb-16 px-4">
                  {creditPackages.map((pkg, index) => (
                    <Card
                      key={index}
                      variant={pkg.popular ? "glow" : "glass"}
                      className={`relative ${pkg.popular ? "border-primary/50 scale-105" : ""}`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="px-3 py-1 bg-gradient-primary text-primary-foreground text-xs font-semibold rounded-full flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {t("คุ้มที่สุด", "Best Value")}
                          </span>
                        </div>
                      )}
                      <CardHeader className="text-center pb-2">
                        <CardTitle className="text-4xl font-bold text-gradient">
                          {pkg.credits.toLocaleString()}
                        </CardTitle>
                        <p className="text-muted-foreground">{t("เครดิต", "Credits")}</p>
                      </CardHeader>
                      <CardContent className="text-center">
                        <div className="mb-6">
                          {pkg.credits === 3000 ? (
                            <div className="flex flex-col items-center">
                              <span className="text-xs text-orange-500 font-bold mb-1">{t("ราคาพิเศษช่วงทดลองระบบ", "Special Trial Price")}</span>
                              <span className="text-[10px] text-orange-400 mb-2">{t("จำกัด คนละ 1 ครั้ง", "Limited 1 per person")}</span>
                              <span className="text-3xl font-bold text-primary">฿{pkg.price.toLocaleString()}</span>
                            </div>
                          ) : pkg.credits === 3400 ? (
                            <div className="flex flex-col items-center">
                              <span className="text-xs text-destructive font-bold mb-1">{t("ลดพิเศษช่วงนี้", "Limited Offer")}</span>
                              <span className="text-sm text-muted-foreground line-through">฿1,200</span>
                              <span className="text-3xl font-bold text-primary">฿{pkg.price.toLocaleString()}</span>
                            </div>
                          ) : (
                            <span className="text-3xl font-bold">฿{pkg.price.toLocaleString()}</span>
                          )}
                        </div>
                        <ul className="space-y-3 text-sm text-muted-foreground mb-6 text-left border-t border-border/50 pt-6">
                          <li className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-xs">
                              {t("Seedance 2.0 (10s): ", "Seedance 2.0 (10s): ")}
                              <b className="text-foreground">{Math.floor(pkg.credits / 330)}</b>
                              {t(" วิดีโอ", " vdo")}
                            </span>
                          </li>
                          <li className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-xs">
                              {t("GPT Image: ", "GPT Image: ")}
                              <b className="text-foreground">{Math.floor(pkg.credits / 4)}</b>
                              {t(" ภาพ", " images")}
                            </span>
                          </li>
                          <li className="flex items-center gap-2">
                            <ImageIcon className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-xs">
                              {t("Nano Banana: ", "Nano Banana: ")}
                              <b className="text-foreground">{Math.floor(pkg.credits / 4)}</b>
                              {t(" ภาพ", " images")}
                            </span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Music className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-xs">
                              {t("Suno Music: ", "Suno Music: ")}
                              <b className="text-foreground">{Math.floor(pkg.credits / 12)}</b>
                              {t(" เพลง", " songs")}
                            </span>
                          </li>
                        </ul>

                        <ul className="space-y-2 text-[11px] text-muted-foreground mb-8 text-left bg-muted/30 p-3 rounded-lg">
                          <li className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-primary flex-shrink-0" />
                            {t("ใช้ได้ทุก AI Model", "All AI Models Included")}
                          </li>
                          <li className="flex items-center gap-2">
                            <Check className="h-3 w-3 text-primary flex-shrink-0" />
                            {t("เครดิตอายุ 1 ปี", "Credits expire in 1 year")}
                          </li>
                        </ul>
                        <Button
                          className="w-full font-bold h-11 rounded-xl"
                          variant={pkg.popular ? "glow" : "outline"}
                          onClick={() => handleBuyCredits(index)}
                        >
                          {t("เลือกแพ็กเกจนี้", "Get Started")}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Payment Flow */
            <div className="max-w-3xl mx-auto">
              {/* Payment Section */}
              <div className="animate-in fade-in zoom-in-95 duration-300">
                <Card className="w-full border-white/20 shadow-2xl overflow-hidden bg-background/90 backdrop-blur-2xl ring-1 ring-white/20">
                  <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent pb-6 pt-8">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-2xl bg-primary/20 text-primary shadow-inner">
                          <QrCode className="h-6 w-6" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl font-bold tracking-tight text-left">{t("ชำระเงิน", "Payment")}</CardTitle>
                          <p className="text-xs text-muted-foreground mt-0.5 text-left">{t("กรุณาชำระเงินเพื่อรับเครดิตเข้าบัญชี", "Please complete payment to receive credits")}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors" onClick={() => setShowPayment(false)}>
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="p-0">
                    <div className="p-6 md:p-8 space-y-8">
                      {/* Guest Info Form - Only for unauthenticated users */}
                      {!user && (
                        <div className="relative group">
                          <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/30 to-purple-500/30 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-500"></div>
                          <div className="relative bg-background/50 backdrop-blur-sm rounded-2xl p-6 border border-white/20 space-y-5">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="h-8 w-1 bg-primary rounded-full"></div>
                              <h3 className="font-bold text-base tracking-tight">{t("ข้อมูลผู้สั่งซื้อ", "Customer Information")}</h3>
                            </div>

                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="guestName" className="text-sm font-medium ml-1 text-left block">{t("ชื่อ-นามสกุล", "Full Name")}</Label>
                                <div className="relative group/input">
                                  <User className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground group-focus-within/input:text-primary transition-colors" />
                                  <Input
                                    id="guestName"
                                    placeholder={t("ระบุชื่อผู้ใช้งาน", "Enter your name")}
                                    className="pl-11 h-11 bg-white/5 border-border/50 focus:border-primary/50 focus:ring-primary/20 rounded-xl transition-all"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                  />
                                </div>
                              </div>

                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="guestEmail" className="text-sm font-medium ml-1 text-left block">{t("อีเมล", "Email")}</Label>
                                  <div className="relative group/input">
                                    <Mail className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground group-focus-within/input:text-primary transition-colors" />
                                    <Input
                                      id="guestEmail"
                                      type="email"
                                      placeholder="example@mail.com"
                                      className="pl-11 h-11 bg-white/5 border-border/50 focus:border-primary/50 focus:ring-primary/20 rounded-xl transition-all"
                                      value={guestEmail}
                                      onChange={(e) => setGuestEmail(e.target.value)}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="guestPhone" className="text-sm font-medium ml-1 text-left block">{t("เบอร์โทรศัพท์", "Phone Number")}</Label>
                                  <div className="relative group/input">
                                    <Phone className="absolute left-3.5 top-3 h-4 w-4 text-muted-foreground group-focus-within/input:text-primary transition-colors" />
                                    <Input
                                      id="guestPhone"
                                      placeholder="08x-xxx-xxxx"
                                      className="pl-11 h-11 bg-white/5 border-border/50 focus:border-primary/50 focus:ring-primary/20 rounded-xl transition-all"
                                      value={guestPhone}
                                      onChange={(e) => setGuestPhone(e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order Summary Section */}
                      <div className="bg-primary/5 rounded-2xl p-5 border border-primary/20 mb-6">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest text-left">{t("รายการสั่งซื้อ", "Credit Package")}</span>
                          <h4 className="font-bold text-foreground text-left">
                            {isTestPayment ? "1 Credit (Test)" : `${creditPackages[selectedPackage!]?.credits.toLocaleString()} Credits`}
                          </h4>
                          <div className="h-px bg-border/30 my-2"></div>
                          <div className="flex justify-between items-end">
                            <span className="text-xs text-muted-foreground font-medium uppercase">{t("ยอดรวมสุทธิ", "Total")}</span>
                            <span className="text-3xl font-black text-primary tracking-tighter">
                              ฿{isTestPayment ? "10" : creditPackages[selectedPackage!]?.price.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Stripe Payment Flow */}
                      {isStripeMode ? (
                        <div className="space-y-6">
                          {!stripeQrUrl ? (
                            // Step 1: Generate QR Code
                            <div className="text-center py-8">
                              <Button
                                variant="glow"
                                size="lg"
                                className="gap-3 h-14 px-8 text-lg font-bold"
                                onClick={handleStripePayment}
                                disabled={stripeLoading}
                              >
                                {stripeLoading ? (
                                  <>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    {t("กำลังสร้าง QR...", "Generating QR...")}
                                  </>
                                ) : (
                                  <>
                                    <QrCode className="h-5 w-5" />
                                    {t("สร้าง QR Code ชำระเงิน", "Generate Payment QR")}
                                  </>
                                )}
                              </Button>
                              <p className="text-xs text-muted-foreground mt-3">
                                {t("ระบบจะสร้าง QR Code สำหรับชำระเงินผ่าน PromptPay", "System will generate PromptPay QR code for payment")}
                              </p>

                            </div>
                          ) : (
                            // Step 2: Show Stripe QR and status
                            <div className="grid md:grid-cols-2 gap-6 items-start">
                              {/* Left side: Stripe QR */}
                              <div className="space-y-4">
                                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                                  <div className="bg-white p-3 rounded-xl inline-block shadow-xl mb-3">
                                    <img
                                      src={stripeQrUrl}
                                      alt="PromptPay QR Code"
                                      className="w-40 h-40 md:w-48 md:h-48 object-contain"
                                    />
                                  </div>
                                  <p className="text-xs font-bold text-primary animate-pulse mb-2">
                                    {t("สแกน QR Code ด้วยแอปธนาคาร", "Scan with your banking app")}
                                  </p>
                                  {stripeExpiresAt && (
                                    <p className="text-[10px] text-muted-foreground">
                                      {t("QR หมดอายุใน 15 นาที", "QR expires in 15 minutes")}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Right side: Payment Status */}
                              <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-1">
                                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">{t("สถานะการชำระเงิน", "Payment Status")}</h3>
                                </div>

                                <div className={`p-6 rounded-2xl border-2 text-center ${paymentStatus === 'completed' ? 'bg-green-500/10 border-green-500/50' :
                                  paymentStatus === 'verifying' ? 'bg-amber-500/10 border-amber-500/50' :
                                  paymentStatus === 'failed' ? 'bg-red-500/10 border-red-500/50' :
                                    'bg-white/5 border-white/20'
                                  }`}>
                                  {paymentStatus === 'pending' && (
                                    <>
                                      <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-3" />
                                      <p className="font-bold text-lg">{t("รอการชำระเงิน", "Waiting for payment")}</p>
                                      <p className="text-xs text-muted-foreground mt-1">{t("กรุณาสแกน QR และชำระเงิน", "Please scan QR and complete payment")}</p>
                                    </>
                                  )}
                                  {paymentStatus === 'completed' && (
                                    <>
                                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg text-green-500">{t("ชำระเงินสำเร็จ!", "Payment Successful!")}</p>
                                      <p className="text-xs text-green-500/70 mt-1">{t("เครดิตถูกเติมเข้าบัญชีแล้ว", "Credits added to your account")}</p>
                                    </>
                                  )}
                                  {paymentStatus === 'verifying' && (
                                    <>
                                      <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg text-amber-500">{t("กำลังยืนยันเครดิต", "Verifying Credits")}</p>
                                      <p className="text-xs text-amber-500/70 mt-1">{t("ชำระเงินสำเร็จแล้ว กำลังรอระบบอัปเดตเครดิต", "Payment completed, waiting for credits to update")}</p>
                                    </>
                                  )}
                                  {paymentStatus === 'failed' && (
                                    <>
                                      <X className="h-12 w-12 text-red-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg text-red-500">{t("การชำระเงินล้มเหลว", "Payment Failed")}</p>
                                      <p className="text-xs text-red-500/70 mt-1">{t("กรุณาลองใหม่อีกครั้ง", "Please try again")}</p>
                                    </>
                                  )}
                                </div>

                                <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                  <div className="flex gap-2">
                                    <Sparkles className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                                    <p className="text-[10px] leading-relaxed text-muted-foreground text-left">
                                      {t("ชำระเงินผ่าน Stripe PromptPay - เครดิตจะถูกเติมอัตโนมัติทันทีหลังชำระเงินสำเร็จ", "Pay via Stripe PromptPay - Credits will be added automatically after successful payment")}
                                    </p>
                                  </div>
                                </div>

                                {paymentStatus === 'pending' && (
                                  <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                      resetStripeState();
                                    }}
                                  >
                                    {t("ยกเลิกและสร้าง QR ใหม่", "Cancel and generate new QR")}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : isBeamMode ? (
                        /* BeamCheckout Payment Flow */
                        <div className="space-y-6">
                          {!beamQrUrl ? (
                            // Step 1: Generate Beam QR Code
                            <div className="text-center py-8">
                              <Button
                                variant="glow"
                                size="lg"
                                className="gap-3 h-14 px-8 text-lg font-bold"
                                onClick={handleBeamPayment}
                                disabled={beamLoading}
                              >
                                {beamLoading ? (
                                  <>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    {t("กำลังสร้าง QR...", "Generating QR...")}
                                  </>
                                ) : (
                                  <>
                                    <QrCode className="h-5 w-5" />
                                    {t("สร้าง QR Code ชำระเงิน", "Generate Payment QR")}
                                  </>
                                )}
                              </Button>
                              <p className="text-xs text-muted-foreground mt-3">
                                {t("ระบบจะสร้าง QR Code สำหรับชำระเงินผ่าน PromptPay (ค่าธรรมเนียม 0%)", "System will generate PromptPay QR code (0% fee)")}
                              </p>
                            </div>
                          ) : (
                            // Step 2: Show Beam QR and payment status
                            <div className="grid md:grid-cols-2 gap-6 items-start">
                              {/* Left side: Beam QR */}
                              <div className="space-y-4">
                                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                                  <div className="bg-white p-3 rounded-xl inline-block shadow-xl mb-3">
                                    <img
                                      src={beamQrUrl}
                                      alt="BeamCheckout PromptPay QR"
                                      className="w-40 h-40 md:w-48 md:h-48 object-contain"
                                    />
                                  </div>
                                  <p className="text-xs font-bold text-cyan-500 animate-pulse mb-2">
                                    {t("สแกน QR Code ด้วยแอปธนาคาร", "Scan with your banking app")}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground">
                                    {t("QR หมดอายุใน 15 นาที", "QR expires in 15 minutes")}
                                  </p>
                                </div>
                              </div>

                              {/* Right side: Payment Status */}
                              <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-1">
                                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">{t("สถานะการชำระเงิน", "Payment Status")}</h3>
                                </div>

                                <div className={`p-6 rounded-2xl border-2 text-center ${paymentStatus === 'completed' ? 'bg-green-500/10 border-green-500/50' :
                                  paymentStatus === 'verifying' ? 'bg-amber-500/10 border-amber-500/50' :
                                  paymentStatus === 'failed' ? 'bg-red-500/10 border-red-500/50' :
                                    'bg-white/5 border-white/20'
                                  }`}>
                                  {paymentStatus === 'pending' && (
                                    <>
                                      <Loader2 className="h-12 w-12 animate-spin text-cyan-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg">{t("รอการชำระเงิน", "Waiting for payment")}</p>
                                      <p className="text-xs text-muted-foreground mt-1">{t("กรุณาสแกน QR และชำระเงิน", "Please scan QR and complete payment")}</p>
                                    </>
                                  )}
                                  {paymentStatus === 'completed' && (
                                    <>
                                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg text-green-500">{t("ชำระเงินสำเร็จ!", "Payment Successful!")}</p>
                                      <p className="text-xs text-green-500/70 mt-1">{t("เครดิตถูกเติมเข้าบัญชีแล้ว", "Credits added to your account")}</p>
                                    </>
                                  )}
                                  {paymentStatus === 'verifying' && (
                                    <>
                                      <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg text-amber-500">{t("กำลังยืนยันเครดิต", "Verifying Credits")}</p>
                                      <p className="text-xs text-amber-500/70 mt-1">{t("ชำระเงินสำเร็จแล้ว กำลังรอระบบอัปเดตเครดิต", "Payment completed, waiting for credits to update")}</p>
                                    </>
                                  )}
                                  {paymentStatus === 'failed' && (
                                    <>
                                      <X className="h-12 w-12 text-red-500 mx-auto mb-3" />
                                      <p className="font-bold text-lg text-red-500">{t("การชำระเงินล้มเหลว", "Payment Failed")}</p>
                                      <p className="text-xs text-red-500/70 mt-1">{t("กรุณาลองใหม่อีกครั้ง", "Please try again")}</p>
                                    </>
                                  )}
                                </div>

                                <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                                  <div className="flex gap-2">
                                    <Sparkles className="h-4 w-4 text-cyan-500 shrink-0 mt-0.5" />
                                    <p className="text-[10px] leading-relaxed text-muted-foreground text-left">
                                      {t("ชำระเงินผ่าน BeamCheckout PromptPay (ค่าธรรมเนียม 0%) - เครดิตจะถูกเติมอัตโนมัติทันทีหลังชำระเงินสำเร็จ", "Pay via BeamCheckout PromptPay (0% fee) - Credits will be added automatically after successful payment")}
                                    </p>
                                  </div>
                                </div>

                                {paymentStatus === 'pending' && (
                                  <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                      resetBeamState();
                                    }}
                                  >
                                    {t("ยกเลิกและสร้าง QR ใหม่", "Cancel and generate new QR")}
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Manual Payment Flow (Original)
                        <div className="grid md:grid-cols-2 gap-6 items-start">
                          {/* Left side: QR & Summary */}
                          <div className="space-y-6">
                            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                              <div className="bg-white p-3 rounded-xl inline-block shadow-xl mb-3">
                                <img
                                  src={paymentSettings?.manualQrCodeUrl || "/kbank-bas.jpg"}
                                  alt="Bank QR Code"
                                  className="w-32 h-32 md:w-36 md:h-36 object-contain"
                                />
                              </div>
                              <p className="text-xs font-bold text-primary animate-pulse mb-2">
                                {t("สแกน QR Code เพื่อชำระเงิน", "Scan QR Code to pay")}
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-[11px] gap-2 rounded-lg border-primary/20 hover:bg-primary/10 hover:text-primary transition-all active:scale-95"
                                onClick={handleSaveQR}
                              >
                                <Download className="h-3.5 w-3.5" />
                                {t("บันทึกภาพ QR Code", "Save QR Image")}
                              </Button>
                            </div>
                          </div>

                          {/* Right side: Bank Details */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-1">
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">{t("ข้อมูลบัญชี", "Bank Details")}</h3>
                            </div>

                            <div className="space-y-3">
                              <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                                <span className="text-[10px] text-muted-foreground font-bold uppercase block mb-1 text-left">{t("ธนาคาร", "Bank")}</span>
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-sm">{paymentSettings?.bankName || "KBANK กสิกรไทย"}</span>
                                  <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                </div>
                              </div>

                              <div className="p-4 rounded-2xl bg-white/10 border border-primary/30 shadow-lg shadow-primary/5 relative overflow-hidden group/copy">
                                <span className="text-[10px] text-primary font-black uppercase block mb-1 text-left">{t("เลขบัญชี", "Account No.")}</span>
                                <div className="flex items-center justify-between gap-2 relative z-10">
                                  <span className="font-mono font-black text-lg tracking-wider text-gradient">{paymentSettings?.bankAccountNumber || "457-2-10138-5"}</span>
                                  <Button
                                    variant="glow"
                                    size="icon"
                                    className="h-8 w-8 rounded-lg shadow-lg active:scale-90 transition-transform"
                                    onClick={() => {
                                      const accountNo = (paymentSettings?.bankAccountNumber || "457-2-10138-5").replace(/-/g, "");
                                      navigator.clipboard.writeText(accountNo);
                                      toast({ title: t("คัดลอกแล้ว", "Copied") });
                                    }}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>

                              <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                                <span className="text-[10px] text-muted-foreground font-bold uppercase block mb-1 text-left">{t("ชื่อบัญชี", "Account Name")}</span>
                                <span className="font-bold text-sm block text-left">{paymentSettings?.bankAccountName || "รณภพ ชนะพิมพ์"}</span>
                              </div>
                            </div>

                            <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                              <div className="flex gap-2">
                                <Clock className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                                <p className="text-[10px] leading-relaxed text-black font-semibold text-left">
                                  {t("โอนจากทุกแบงค์อนุมัติทันที ส่วน BBL รอ 10 นาที", "Instant approval for all banks except BBL (10 min wait)")}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Upload Slip - Only for Manual mode */}
                      {!isStripeMode && !isBeamMode && (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <div
                            onClick={handleUploadClick}
                            className={`group/upload relative overflow-hidden border-2 border-dashed rounded-2xl p-6 md:p-8 text-center transition-all cursor-pointer ${uploading
                              ? "border-primary/50 bg-primary/10 cursor-wait"
                              : isUploaded
                                ? "border-green-500/50 bg-green-500/10"
                                : "border-border hover:border-primary/50 hover:bg-white/5"
                              }`}
                          >
                            {uploading ? (
                              <div className="flex flex-col items-center py-2">
                                <Loader2 className="h-16 w-16 text-primary animate-spin mb-4" />
                                <p className="text-lg font-bold text-primary">{t("กำลังอัพโหลด...", "Uploading...")}</p>
                              </div>
                            ) : isUploaded ? (
                              <div className="flex flex-col items-center py-2 animate-in fade-in zoom-in duration-300">
                                <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                                </div>
                                <p className="text-xl font-black text-green-500 tracking-tight">{t("อัพโหลดสำเร็จ", "Upload Successful")}</p>
                                <p className="text-sm text-green-500/60 mt-1 font-medium">{t("คลิกที่นี่หากต้องการเปลี่ยนรูปสลิป", "Click to change slip image")}</p>
                              </div>
                            ) : (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover/upload:opacity-100 transition-opacity"></div>
                                <div className="relative z-10">
                                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover/upload:scale-110 group-hover/upload:bg-primary/20 transition-all duration-500">
                                    <Upload className="h-7 w-7 text-primary" />
                                  </div>
                                  <p className="text-lg font-bold mb-1 tracking-tight">{t("อัพโหลดสลิปการโอนเงิน", "Upload payment slip")}</p>
                                  <p className="text-xs text-muted-foreground font-medium px-4">
                                    {t("กรุณาอัพโหลดหลักฐานการโอนเงินเพื่อดำเนินการตรวจสอบ", "Please upload transfer proof for verification")}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>

                  {/* Footer - Different for Manual vs Stripe vs Beam */}
                  {!isStripeMode && !isBeamMode && (
                    <CardFooter className="flex gap-4 p-6 md:p-8 bg-muted/30 border-t border-border/50">
                      <Button
                        variant="outline"
                        className="flex-1 h-12 rounded-xl font-bold border-white/10 hover:bg-white/5 active:scale-95 transition-all text-muted-foreground"
                        onClick={() => {
                          setShowPayment(false);
                          setIsUploaded(false);
                        }}
                      >
                        {t("ยกเลิก", "Cancel")}
                      </Button>
                      <Button
                        variant="glow"
                        className="flex-[1.5] h-12 rounded-xl font-black text-base shadow-xl shadow-primary/20 active:scale-95 transition-all group/btn"
                        onClick={() => {
                          if (!isUploaded) {
                            toast({
                              variant: "destructive",
                              title: t("กรุณาอัพโหลดสลิป", "Please upload slip"),
                              description: t("คุณต้องอัพโหลดสลิปโอนเงินก่อนแจ้งโอน", "You must upload the slip before confirming payment")
                            });
                            return;
                          }

                          // Validation for Guest
                          if (!user) {
                            if (!guestName || !guestEmail || !guestPhone) {
                              toast({
                                variant: "destructive",
                                title: t("ระบุข้อมูลไม่ครบ", "Information missing"),
                                description: t("กรุณาระบุชื่อ, อีเมล และเบอร์โทรศัพท์", "Please provide name, email, and phone number")
                              });
                              return;
                            }
                          }
                          toast({
                            title: t("แจ้งโอนสำเร็จ", "Payment Notified"),
                            description: t("เจ้าหนี้จะตรวจสอบและเติมเครดิตให้คุณโดยเร็ว", "We will verify and add credits soon")
                          });
                          setShowPayment(false);
                        }}
                      >
                        <span className="mr-2">{t("โอนแล้ว", "I've Paid")}</span>
                        <CheckCircle2 className="h-5 w-5 group-hover/btn:scale-110 transition-transform" />
                      </Button>
                    </CardFooter>
                  )}

                  {/* Stripe mode: Simple cancel footer */}
                  {isStripeMode && !stripeQrUrl && (
                    <CardFooter className="flex gap-4 p-6 md:p-8 bg-muted/30 border-t border-border/50">
                      <Button
                        variant="outline"
                        className="flex-1 h-12 rounded-xl font-bold border-white/10 hover:bg-white/5 active:scale-95 transition-all text-muted-foreground"
                        onClick={() => {
                          setShowPayment(false);
                          resetStripeState();
                        }}
                      >
                        {t("ยกเลิก", "Cancel")}
                      </Button>
                    </CardFooter>
                  )}

                  {/* Beam mode: Simple cancel footer */}
                  {isBeamMode && !beamQrUrl && (
                    <CardFooter className="flex gap-4 p-6 md:p-8 bg-muted/30 border-t border-border/50">
                      <Button
                        variant="outline"
                        className="flex-1 h-12 rounded-xl font-bold border-white/10 hover:bg-white/5 active:scale-95 transition-all text-muted-foreground"
                        onClick={() => {
                          setShowPayment(false);
                          resetBeamState();
                        }}
                      >
                        {t("ยกเลิก", "Cancel")}
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      {/* Login Prompt Dialog */}
      <AlertDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <AlertDialogContent className="max-w-md rounded-3xl border-white/20 bg-background/95 backdrop-blur-xl">
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <User className="h-8 w-8" />
            </div>
            <AlertDialogTitle className="text-center text-2xl font-bold">
              {t("กรุณาเข้าสู่ระบบ", "Please Login")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base">
              {t(
                "กรุณาสมัครสมาชิกหรือเข้าสู่ระบบเพื่อสะสมเครดิตและติดตามประวัติการใช้งานของคุณ",
                "Please register or login to collect credits and track your usage history."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            <div className="grid grid-cols-2 gap-3 w-full mt-4">
              <Button
                variant="glow"
                className="w-full rounded-xl font-bold"
                onClick={() => navigate("/register")}
              >
                {t("สมัครสมาชิกฟรี", "Register Free")}
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-xl font-bold border-white/20"
                onClick={() => navigate("/login")}
              >
                {t("เข้าสู่ระบบ", "Login")}
              </Button>
            </div>
            <Button
              variant="ghost"
              className="w-full mt-2 text-muted-foreground hover:text-foreground text-xs"
              onClick={handleCloseLoginPrompt}
            >
              {t("ปิดหน้าต่างนี้", "Close this window")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Pricing;
